import { useQuery } from "@tanstack/react-query";
import { MediaPlayer, MediaProvider } from "@vidstack/react";
import { getVimeoVideoLink } from "@/api/api";
import type { VimeoResponse } from "@/api/vimeo.types";
import { cn } from "@/components/utils/utils";

interface VideoPreviewPlayerProps {
	vimeoId: number;
	vimeoData?: VimeoResponse;
	isLoading?: boolean;
	className?: string;
}

/**
 * Prosty komponent do wyświetlania video z Vimeo w inline okienku
 * Używa vidstack MediaPlayer do odtwarzania HLS streamu z Vimeo API
 */
export const VideoPreviewPlayer = ({
	vimeoId,
	vimeoData: vimeoDataProp,
	isLoading: isLoadingProp,
	className,
}: VideoPreviewPlayerProps): JSX.Element => {
	// Jeśli vimeoData nie jest przekazane jako prop, pobierz je samodzielnie
	const { data: vimeoDataQuery, isLoading: isLoadingQuery } = useQuery<VimeoResponse | undefined>({
		queryKey: ["vimeoVideoLink", vimeoId],
		queryFn: () => getVimeoVideoLink(vimeoId.toString()),
		enabled: !!vimeoId && !vimeoDataProp,
	});

	const vimeoData = vimeoDataProp || vimeoDataQuery;
	const isLoading = isLoadingProp !== undefined ? isLoadingProp : isLoadingQuery;

	if (isLoading) {
		return (
			<div
				className={cn(
					"w-full bg-black rounded-lg relative flex items-center justify-center",
					className
				)}
				style={{ paddingTop: "56.25%" }} // 16:9 aspect ratio
			>
				<div className="absolute inset-0 flex items-center justify-center text-white">
					<p>Ładowanie wideo...</p>
				</div>
			</div>
		);
	}

	if (!vimeoData?.play?.hls?.link) {
		return (
			<div
				className={cn(
					"w-full bg-gray-200 rounded-lg relative flex items-center justify-center",
					className
				)}
				style={{ paddingTop: "56.25%" }}
			>
				<div className="absolute inset-0 flex items-center justify-center text-gray-600">
					<p>Nie udało się załadować wideo</p>
				</div>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"w-full relative rounded-lg overflow-hidden bg-black",
				className
			)}
		>
			<MediaPlayer
				src={{
					src: vimeoData.play.hls.link,
					type: "application/x-mpegurl",
				}}
				controls
				playsInline
				className="absolute inset-0 w-full h-full"
			>
				<MediaProvider />
			</MediaPlayer>
		</div>
	);
};

